import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { useScrollToTop, scrollToTop } from "@/hooks/useScrollToTop";
import { articles } from "@/mocks/articles";
import { SITE } from "@/config/site";

export default function Article() {
  useScrollToTop();
  const { id } = useParams();
  const article = articles.find((a) => a.id === id) || articles[0];
  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      image: article.cover,
      datePublished: article.date,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: SITE.name },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.baseUrl}/article/${article.id}` },
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE.baseUrl },
        {
          "@type": "ListItem",
          position: 2,
          name: article.categoryName,
          item: `${SITE.baseUrl}/category/${article.categoryId}`,
        },
        { "@type": "ListItem", position: 3, name: article.title, item: `${SITE.baseUrl}/article/${article.id}` },
      ],
    },
  ];

  useSeo({
    title: `${article.title} - 抖漫资讯`,
    description: `${article.summary} ${SITE.keywords}`,
    keywords: `${article.tags.join(",")},抖漫资讯`,
    path: `/article/${article.id}`,
    jsonLd: articleJsonLd,
  });

  return (
    <Layout>
      <article className="pt-28 pb-14">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <Reveal>
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: article.categoryName, to: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </Reveal>

          {/* Header */}
          <Reveal delay={80}>
            <header>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-md bg-background-800 text-primary-400 text-sm whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-md bg-background-800 text-foreground-400 text-sm whitespace-nowrap"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 leading-snug mb-5">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line text-primary-400"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views}阅读
                </span>
              </div>
            </header>
          </Reveal>

          {/* Cover */}
          <Reveal delay={120}>
            <figure className="overflow-hidden rounded-xl mb-8">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} 抖漫资讯`}
                className="w-full object-cover aspect-video"
              />
            </figure>
          </Reveal>

          {/* Body */}
          <Reveal delay={160}>
            <div className="space-y-5">
              {article.content.map((para, idx) => (
                <p
                  key={idx}
                  className="text-base md:text-lg text-foreground-300 leading-loose"
                >
                  {para}
                </p>
              ))}
            </div>
          </Reveal>

          {/* Tags */}
          <Reveal delay={200}>
            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200/20 pt-6">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((t) => (
                <Link
                  key={t}
                  to={`/category/${article.categoryId}`}
                  onClick={scrollToTop}
                  className="px-3 py-1 rounded-md bg-background-800 text-foreground-300 text-sm hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  #{t}
                </Link>
              ))}
            </div>

            {/* Share */}
            <div className="mt-6 flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分享：</span>
              {[
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-tiktok-line", label: "抖音" },
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-link", label: "复制链接" },
              ].map((s, idx) => (
                <a
                  key={idx}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  aria-label={`分享到${s.label}`}
                  className="w-9 h-9 rounded-lg bg-background-800 flex items-center justify-center text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors"
                >
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </article>

      {/* Related */}
      <section className="pb-16">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <Reveal>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-50 mb-6">相关文章</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-1 gap-5">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} horizontal />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}